import React, { useMemo } from 'react';
import { MapPin, RefreshCw, AlertTriangle, Info } from 'lucide-react';
import {
  buildLocationRows, isPerLocationBilling, totalNextInvoiceCents,
  type DriftLocationLike, type LocationRequestLike, type LocationBillingRow,
} from './locationsBillingModel';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * LOCATIONS & BILLING — what each store costs, per store.
 *
 * ---------------------------------------------------------------------------
 * READ-ONLY, DELIBERATELY
 * ---------------------------------------------------------------------------
 * The card it replaces carried three money actions — Make free, Change price,
 * Deactivate — all of which drove `location-requests.stripeSubscriptionId`, the
 * per-location subscription of the model that PRECEDED per-location billing.
 * That field is null on every migrated store, so:
 *
 *  - "Make free" wrote `isFree: true` and reported "Subscription cancelled",
 *    while the store's real subscription carried on charging.
 *  - "Deactivate" could not cancel anything either, but it DID set the
 *    location's status to inactive — taking a live store offline while its
 *    subscription kept billing.
 *
 * They are gone rather than disabled. A change to what a store pays belongs to
 * the Plan assignment card, which composes packages per location.
 *
 * ---------------------------------------------------------------------------
 * WHY THE AMOUNT IS "NEXT INVOICE" AND NOT "/mo"
 * ---------------------------------------------------------------------------
 * Promotions on this platform are subscription SCHEDULE PHASES, so summing item
 * amounts gives the LIST price with every offer stripped out — verified once at
 * $257.92 of items against a $209.89 invoice. The figure here is the upcoming
 * invoice total, straight from Stripe. That includes prorations mid-cycle, so
 * it is a NEXT INVOICE, not a monthly rate, and it says so.
 */

const money = (cents?: number) =>
  (typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '—');

const day = (unixSeconds?: number) => (unixSeconds
  ? new Date(unixSeconds * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  : null);

interface Props {
  /** `null` — the billing read failed or has not run yet. */
  driftLocations: DriftLocationLike[] | null;
  driftLoading?: boolean;
  driftError?: string | null;
  requests: LocationRequestLike[];
  requestsLoading?: boolean;
  freeQuota?: number;
  dealFreeQuota?: number;
  additionalLocationPriceCents?: number;
  onRefresh: () => void;
  onOpen: (row: LocationBillingRow) => void;
  onReviewDocs: (row: LocationBillingRow) => void;
  onApprove: (row: LocationBillingRow) => void;
}

const LocationsBillingPanel: React.FC<Props> = ({
  driftLocations, driftLoading, driftError, requests, requestsLoading,
  freeQuota, dealFreeQuota, additionalLocationPriceCents,
  onRefresh, onOpen, onReviewDocs, onApprove,
}) => {
  const rows = useMemo(
    () => buildLocationRows({ driftLocations, requests: requests || [] }),
    [driftLocations, requests],
  );
  const perLocation = useMemo(() => isPerLocationBilling(driftLocations), [driftLocations]);
  const billedRows = useMemo(() => rows.filter((r) => typeof r.nextInvoiceCents === 'number'), [rows]);
  const total = useMemo(() => totalNextInvoiceCents(rows), [rows]);
  const loading = driftLoading || requestsLoading;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
      <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <MapPin size={18} className="text-stone-600" />
          <h3 className="font-semibold text-stone-800">Locations &amp; Billing</h3>
          {perLocation ? (
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
              Per-location billing
            </span>
          ) : (
            <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full font-medium">
              Free quota: {freeQuota ?? 1}
              {Number.isInteger(dealFreeQuota) ? ` (deal: ${dealFreeQuota})` : ''}
            </span>
          )}
          {!perLocation && Number.isInteger(additionalLocationPriceCents) && (
            <span className="text-xs bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full font-medium">
              Extra location: {money(additionalLocationPriceCents)}/mo
            </span>
          )}
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="p-2 text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100 transition-colors"
          aria-label="Refresh locations"
        >
          {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <RubyLoader variant="section" />
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-stone-400">No locations yet</div>
      ) : (
        <div className="divide-y divide-stone-100">
          {rows.map((row) => (
            <div key={row.key} className="px-5 py-3.5 flex items-center gap-4 hover:bg-stone-50/50 transition-colors flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-stone-800">{row.name}</p>
                  {row.status && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      row.status === 'active' ? 'bg-emerald-100 text-emerald-700'
                        : row.pending ? 'bg-amber-100 text-amber-700'
                          : row.status === 'grace' ? 'bg-red-100 text-red-700'
                            : 'bg-stone-100 text-stone-600'
                    }`}>
                      {String(row.status).replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
                {row.subscriptionId && (
                  <p className="text-[11px] font-mono text-stone-400 mt-0.5">{row.subscriptionId}</p>
                )}
              </div>

              <div className="text-right min-w-[130px]">
                {typeof row.nextInvoiceCents === 'number' ? (
                  <>
                    <p className="tabular-nums text-sm font-semibold text-stone-800">
                      {money(row.nextInvoiceCents)}
                    </p>
                    <p className="text-[11px] text-stone-400">
                      next invoice{day(row.nextInvoiceAt) ? ` · ${day(row.nextInvoiceAt)}` : ''}
                    </p>
                  </>
                ) : (
                  /* NEVER "Free". An amount that could not be read has not been
                     shown to be nothing — the whole reason this card was rebuilt. */
                  <p className="text-[11px] text-stone-500 max-w-[200px]">{row.unavailable}</p>
                )}
              </div>

              <div className="flex gap-2">
                {row.pending ? (
                  <>
                    <button
                      onClick={() => onReviewDocs(row)}
                      className="text-xs px-2.5 py-1.5 border border-stone-300 text-stone-600 hover:bg-stone-100 rounded-lg font-medium"
                    >
                      Review docs
                    </button>
                    <button
                      onClick={() => onApprove(row)}
                      className="text-xs px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
                    >
                      Approve…
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => onOpen(row)}
                    disabled={!row.requestId}
                    title={row.requestId ? undefined : 'This store has no location record to open.'}
                    className="text-xs px-2.5 py-1.5 border border-stone-300 text-stone-600 hover:bg-stone-100 rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Open
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="px-5 py-3 border-t border-stone-100 space-y-2">
        {billedRows.length > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-stone-500">
              Next invoices across {billedRows.length} location{billedRows.length === 1 ? '' : 's'}
            </span>
            <span className="font-semibold tabular-nums text-stone-800">{money(total)}</span>
          </div>
        )}
        {driftError && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{driftError} — amounts below come from the location records, not from Stripe.</span>
          </div>
        )}
        {perLocation && (
          <div className="flex items-start gap-2 text-xs text-stone-500">
            <Info size={13} className="mt-0.5 shrink-0" />
            <span>
              Each store bills on its own subscription. Amounts are the next invoice from Stripe,
              offers included. To change what a store pays, use Plan assignment on the Finance tab.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationsBillingPanel;
